/**
 * Formatação pt-BR v2 (extraída de MetaOverallV2/lib/format.ts, sem o "modo de moeda" da ROI, que ficou lá).
 * Sinal negativo sempre U+2212 (−). Funções puras (testes em format.test.ts).
 */
export const MINUS = '−';

export const nf = (v: number | null | undefined, d: number) =>
    (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });

export const int = (v: number | null | undefined) => Math.round(v || 0).toLocaleString('pt-BR');
export const k = (v: number | null | undefined) => ((v || 0) >= 1000 ? nf((v || 0) / 1000, 1) + 'k' : int(v));
export const pct = (v: number | null | undefined, d = 2) => nf(v, d) + '%';
export const hh = (h: number) => String(h).padStart(2, '0');

const finite = (v: number | null | undefined): v is number => v != null && Number.isFinite(v);

/** "R$ 1.234,56" / "−R$ 12,00" (sem conversão). */
export const brl = (v: number | null | undefined, d = 2) => ((v || 0) < 0 ? MINUS : '') + 'R$ ' + nf(Math.abs(v || 0), d);

/** "US$ 41,20" (mockup do WhatsApp; por clique usa 4 casas). null → "—". */
export const usd = (v: number | null | undefined, d = 2) => (finite(v) ? (v < 0 ? MINUS : '') + 'US$ ' + nf(Math.abs(v), d) : '—');

/** "94,8%": 1 casa fixa com vírgula. null/não finito → "—". */
export const pct1 = (v: number | null | undefined) => (finite(v) ? nf(v, 1) + '%' : '—');

/** Contagem do WhatsApp (`num` do mockup): null → "—"; |v| ≥ 10000 → "12,4K" (até 1 casa); senão "1.840". */
export const numK = (v: number | null | undefined): string => {
    if (!finite(v)) return '—';
    if (Math.abs(v) >= 10000) return (v < 0 ? MINUS : '') + (Math.abs(v) / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + 'K';
    return (v < 0 ? MINUS : '') + Math.round(Math.abs(v)).toLocaleString('pt-BR');
};

/** "+1.234" / "−12" (U+2212) / "0". */
export const signedInt = (v: number) => (v > 0 ? '+' : v < 0 ? MINUS : '') + Math.round(Math.abs(v)).toLocaleString('pt-BR');

/** "2026-09-21" → "21/09". */
export const dayLbl = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/** "2026-09-21" → "21/09/2026". */
export const dayFull = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

export const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;
export const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'] as const;
export const weekday = (iso: string) => WEEKDAYS[new Date(iso + 'T12:00:00').getDay()];
/** "sáb, 11/10/2026" (data longa do tooltip). */
export const dayLong = (iso: string) => `${weekday(iso)}, ${dayFull(iso)}`;

/** Datas entre from e to (inclusive), ISO. */
export const daysBetween = (from: string, to: string): string[] => {
    const out: string[] = [];
    const d = new Date(from + 'T12:00:00');
    const end = new Date(to + 'T12:00:00');
    while (d <= end) {
        out.push(d.toISOString().slice(0, 10));
        d.setDate(d.getDate() + 1);
    }
    return out;
};

export const addDays = (iso: string, n: number) => {
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};

export const todayISO = () => {
    const d = new Date();
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};
